import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './Leaderboard.css';
import './PlayerLeaderboard.css';
import { getDefaultClusterConfig } from '../utils/clusterConfig';
import PlayerDetailModal from './PlayerDetailModal';
import Podium from './Podium';
import {
  fmtInt,
  fmtHours,
  getRankClass,
  activateOnKey,
  RankMark,
  SectionHead,
  SearchBox,
  SelectPlate,
  SortableTh,
  ErrorState,
  EmptyState,
  Pagination,
  displayName
} from './LeaderboardParts';

// What the podium shows for each sort. No podium for Deaths: a "champion" of dying reads wrong.
const PODIUM_STAT = {
  'Kills': { label: 'kills', value: (p) => fmtInt(p.PlayerKills) },
  'Tamed Dino Kills': { label: 'tame kills', value: (p) => fmtInt(p.DinoKills) },
  'Time Played': { label: 'hours', value: (p) => fmtHours(p.PlayTime) },
};

const PlayerLeaderboard = ({ initialParams = {}, updateURL = () => {} }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState(initialParams.search || '');
  const [filter, setFilter] = useState(initialParams.filter || 'Kills');
  const [cluster, setCluster] = useState(initialParams.cluster || 'SOLO');
  const [currentPage, setCurrentPage] = useState(initialParams.page || 0);
  const [pagination, setPagination] = useState({});
  const [clusterDropdownOpen, setClusterDropdownOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [availableClusters, setAvailableClusters] = useState([{ key: 'SOLO', display: 'Solos' }]);
  const [searchMode, setSearchMode] = useState(!!initialParams.search);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeAgo, setTimeAgo] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Refs for dropdown containers
  const clusterDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle if dropdowns are open
      if (!clusterDropdownOpen && !filterDropdownOpen) return;
      
      if (clusterDropdownRef.current && !clusterDropdownRef.current.contains(event.target)) {
        setClusterDropdownOpen(false);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
    };

    // Only add listener when dropdowns are open
    if (clusterDropdownOpen || filterDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [clusterDropdownOpen, filterDropdownOpen]);

  // Sync state to URL
  useEffect(() => {
    updateURL({ tab: 'player', cluster, filter, page: currentPage, search: searchMode ? search : '' });
  }, [cluster, filter, currentPage, search, searchMode, updateURL]);

  // Auto-refresh "last updated" display every 30s
  useEffect(() => {
    if (!lastUpdated) return;
    const updateTimeAgo = () => {
      const seconds = Math.floor((Date.now() - new Date(lastUpdated).getTime()) / 1000);
      if (seconds < 60) setTimeAgo('just now');
      else if (seconds < 3600) setTimeAgo(`${Math.floor(seconds / 60)}m ago`);
      else setTimeAgo(`${Math.floor(seconds / 3600)}h ago`);
    };
    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, 30000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  const filterOptions = [
    'Kills',
    'Deaths',
    'Tamed Dino Kills',
    'Time Played'
  ];

  // Fetch available clusters on component mount
  useEffect(() => {
    const fetchClusters = async () => {
      try {
        const response = await axios.get('/api/ark/player_rankings/clusters');
        if (response.data.clusters && response.data.clusters.length > 0) {
          const clusters = [...response.data.clusters, { key: 'ALL', display: 'All Clusters' }];
          setAvailableClusters(clusters);
          const currentClusterExists = clusters.find(c => c.key === cluster);
          if (!currentClusterExists) {
            setCluster(clusters[0].key);
          }
        } else {
          console.warn('API returned empty clusters array, using fallback');
          setAvailableClusters([...getDefaultClusterConfig(), { key: 'ALL', display: 'All Clusters' }]);
        }
      } catch (error) {
        console.error('Error fetching available clusters:', error);
        setAvailableClusters([...getDefaultClusterConfig(), { key: 'ALL', display: 'All Clusters' }]);
      }
    };
    fetchClusters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = cluster === 'ALL' ? '/api/ark/player_rankings/all' : '/api/ark/player_rankings';
      const response = await axios.get(endpoint, {
        params: {
          search: '',
          filter: filter,
          ...(cluster !== 'ALL' && { cluster }),
          page: currentPage
        }
      });
      setData(response.data.ranking_data);
      setPagination(response.data.pagination);
      if (response.data.lastUpdated) setLastUpdated(response.data.lastUpdated);
      setError(false);
    } catch (error) {
      console.error('Error fetching player data:', error);
      setData([]);
      setPagination({});
      setError(true);
    }
    setLoading(false);
  }, [filter, cluster, currentPage]);

  const searchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = cluster === 'ALL' ? '/api/ark/player_rankings/all' : '/api/ark/player_rankings';
      const response = await axios.get(endpoint, {
        params: {
          search: search,
          filter: filter,
          ...(cluster !== 'ALL' && { cluster }),
          page: currentPage
        }
      });
      setData(response.data.ranking_data);
      setPagination(response.data.pagination);
      if (response.data.lastUpdated) setLastUpdated(response.data.lastUpdated);
      setError(false);
    } catch (error) {
      console.error('Error fetching player data:', error);
      setData([]);
      setPagination({});
      setError(true);
    }
    setLoading(false);
  }, [search, filter, cluster, currentPage]);

  const searchDataWithFilter = useCallback(async (filterToUse) => {
    setLoading(true);
    try {
      const endpoint = cluster === 'ALL' ? '/api/ark/player_rankings/all' : '/api/ark/player_rankings';
      const response = await axios.get(endpoint, {
        params: {
          search: search,
          filter: filterToUse,
          ...(cluster !== 'ALL' && { cluster }),
          page: currentPage
        }
      });
      setData(response.data.ranking_data);
      setPagination(response.data.pagination);
      if (response.data.lastUpdated) setLastUpdated(response.data.lastUpdated);
      setError(false);
    } catch (error) {
      console.error('Error fetching player data:', error);
      setData([]);
      setPagination({});
      setError(true);
    }
    setLoading(false);
  }, [search, cluster, currentPage]);

  const searchDataWithCluster = useCallback(async (clusterToUse) => {
    setLoading(true);
    try {
      const endpoint = clusterToUse === 'ALL' ? '/api/ark/player_rankings/all' : '/api/ark/player_rankings';
      const response = await axios.get(endpoint, {
        params: {
          search: search,
          filter: filter,
          ...(clusterToUse !== 'ALL' && { cluster: clusterToUse }),
          page: currentPage
        }
      });
      setData(response.data.ranking_data);
      setPagination(response.data.pagination);
      if (response.data.lastUpdated) setLastUpdated(response.data.lastUpdated);
      setError(false);
    } catch (error) {
      console.error('Error fetching player data:', error);
      setData([]);
      setPagination({});
      setError(true);
    }
    setLoading(false);
  }, [search, filter, currentPage]);

  useEffect(() => {
    if (!searchMode) {
      fetchData();
    } else {
      searchData();
    }
  }, [fetchData, searchData, searchMode]);

  const cleanTribeName = (name) => {
    if (!name) return null;
    const fixed = displayName(name);
    return fixed.startsWith('Tribe of ') ? fixed.slice(9) : fixed;
  };

  const formatKD = (kills, deaths) => {
    if (deaths === 0 && kills > 0) return <span className="kd-perfect">{kills}.0</span>;
    if (deaths === 0) return '0.0';
    return (kills / deaths).toFixed(1);
  };

  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setSearchMode(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setCurrentPage(0);
      setSearchMode(true);
    }, 300);
  };

  const handleSearchSubmit = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setCurrentPage(0);
    setSearchMode(true);
    searchData();
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(0);
    setFilterDropdownOpen(false);
    // If there's a search term, use searchData and stay in search mode
    if (search.trim()) {
      // Force a new search with the updated filter by calling searchData directly
      // We need to use the newFilter value directly since setFilter is asynchronous
      searchDataWithFilter(newFilter);
    } else {
      setSearchMode(false);
    }
  };

  const handleClusterChange = (newCluster) => {
    setCluster(newCluster);
    setCurrentPage(0);
    setClusterDropdownOpen(false);
    // If there's a search term, use searchData and stay in search mode
    if (search.trim()) {
      // Force a new search with the updated cluster by calling searchData directly
      // We need to use the newCluster value directly since setCluster is asynchronous
      searchDataWithCluster(newCluster);
    } else {
      setSearchMode(false);
    }
  };

  const clusterDisplay = availableClusters.find(c => c.key === cluster)?.display || cluster;
  // The top three get the podium; the table then starts at #4.
  const showPodium = !loading && !error && currentPage === 0 && !(searchMode && search.trim()) && !!PODIUM_STAT[filter];
  const sortedCls = (option) => (filter === option ? ' is-sorted' : '');
  const emptyMessage = searchMode && search.trim()
    ? `No players found matching "${search}"`
    : 'Servers have recently wiped. Please check again later.';
  const emptyKicker = searchMode && search.trim() ? '// No results' : '// No data';
  const tribeOrCluster = (item) => (cluster === 'ALL'
    ? item.ClusterDisplay
    : (cleanTribeName(item.TribeName) || <span className="tribe-solo">Solo</span>));

  return (
    <div className="lb lb--player player-leaderboard">
      <SectionHead kicker="// Player rankings" title={clusterDisplay} timeAgo={timeAgo} />

      <div className="controls">
        <SearchBox
          value={search}
          placeholder="Search for Players"
          onChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
        />

        <div className="filters">
          <SelectPlate
            groupRef={clusterDropdownRef}
            label="Cluster"
            value={clusterDisplay}
            open={clusterDropdownOpen}
            onToggle={() => setClusterDropdownOpen(!clusterDropdownOpen)}
            options={availableClusters.map(option => ({ key: option.key, label: option.display }))}
            selectedKey={cluster}
            onSelect={handleClusterChange}
          />
          <SelectPlate
            groupRef={filterDropdownRef}
            label="Sort by"
            value={filter}
            open={filterDropdownOpen}
            onToggle={() => setFilterDropdownOpen(!filterDropdownOpen)}
            options={filterOptions.map(option => ({ key: option, label: option }))}
            selectedKey={filter}
            onSelect={handleFilterChange}
          />
        </div>
      </div>

      {showPodium && (
        <Podium
          entries={data
            .map((item, index) => ({ item, rank: Number(item.rank) || index + 1 }))
            .filter(({ rank }) => rank <= 3)
            .map(({ item, rank }) => ({
              rank,
              name: displayName(item.PlayerName),
              sub: cluster === 'ALL' ? item.ClusterDisplay : (cleanTribeName(item.TribeName) || 'Solo'),
              statLabel: PODIUM_STAT[filter].label,
              statValue: PODIUM_STAT[filter].value(item),
              onOpen: () => setSelectedPlayer(item.PlayerName),
              openLabel: 'Open player details',
            }))}
        />
      )}

      <div className="table-container clip-corner">
        {/* Desktop Table */}
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th scope="col" className="col-rank">Rank</th>
              <th scope="col" className="col-name">Player</th>
              <th scope="col" className="col-sub">{cluster === 'ALL' ? 'Cluster' : 'Tribe'}</th>
              <SortableTh className="col-num" label="Kills" active={filter === 'Kills'} onClick={() => handleFilterChange('Kills')} />
              <SortableTh className="col-num" label="Deaths" active={filter === 'Deaths'} onClick={() => handleFilterChange('Deaths')} />
              <th scope="col" className="col-num">K/D</th>
              <SortableTh className="col-num" label="Tamed Dino Kills" active={filter === 'Tamed Dino Kills'} onClick={() => handleFilterChange('Tamed Dino Kills')} />
              <SortableTh className="col-num" label="Time Played" active={filter === 'Time Played'} onClick={() => handleFilterChange('Time Played')} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={`skel-${i}`} className="skeleton-row">
                  <td className="col-rank"><div className="skeleton-cell skeleton-rank" /></td>
                  <td className="col-name"><div className="skeleton-cell skeleton-name" /></td>
                  <td className="col-sub"><div className="skeleton-cell skeleton-name" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                </tr>
              ))
            ) : error ? (
              <tr className="state-row">
                <td colSpan="8">
                  <ErrorState onRetry={() => fetchData()} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr className="state-row">
                <td colSpan="8">
                  <EmptyState kicker={emptyKicker}>{emptyMessage}</EmptyState>
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const rank = Number(item.rank) || index + 1;
                if (showPodium && rank <= 3) return null; // already on the podium
                const open = () => setSelectedPlayer(item.PlayerName);
                return (
                  <tr
                    key={index}
                    className={`clickable-row ${getRankClass(rank)}`}
                    style={{ '--i': index }}
                    onClick={open}
                    onKeyDown={activateOnKey(open)}
                    tabIndex={0}
                    aria-haspopup="dialog"
                  >
                    <td className="col-rank"><RankMark rank={rank} /></td>
                    <td className="col-name"><span className="name-text player-name">{displayName(item.PlayerName)}</span></td>
                    <td className={cluster === 'ALL' ? 'col-sub cluster-tag' : 'col-sub tribe-cell'}>{tribeOrCluster(item)}</td>
                    <td className={`col-num${sortedCls('Kills')}`}>{fmtInt(item.PlayerKills)}</td>
                    <td className={`col-num${sortedCls('Deaths')}`}>{fmtInt(item.DeathByPlayer)}</td>
                    <td className="col-num">{formatKD(item.PlayerKills, item.DeathByPlayer)}</td>
                    <td className={`col-num${sortedCls('Tamed Dino Kills')}`}>{fmtInt(item.DinoKills)}</td>
                    <td className={`col-num${sortedCls('Time Played')}`}>{fmtHours(item.PlayTime)}<span className="unit"> hrs</span></td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Mobile Cards */}
        <div className="mobile-cards">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={`mskel-${i}`} className="mobile-card skeleton-card clip-corner-sm">
                <div className="skeleton-cell skeleton-name" style={{ width: '60%', marginBottom: 12 }} />
                <div className="mobile-stats">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div key={j} className="mobile-stat"><div className="skeleton-cell skeleton-num" /></div>
                  ))}
                </div>
              </div>
            ))
          ) : error ? (
            <ErrorState onRetry={() => fetchData()} />
          ) : data.length === 0 ? (
            <EmptyState kicker={emptyKicker}>{emptyMessage}</EmptyState>
          ) : (
            data.map((item, index) => {
              const rank = Number(item.rank) || index + 1;
                if (showPodium && rank <= 3) return null; // already on the podium
              const open = () => setSelectedPlayer(item.PlayerName);
              return (
                <div
                  key={index}
                  className={`mobile-card clip-corner-sm clickable-row ${getRankClass(rank)}`}
                  style={{ '--i': index }}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  onClick={open}
                  onKeyDown={activateOnKey(open)}
                >
                  <div className="mobile-card-header">
                    <div className="mobile-rank"><RankMark rank={rank} /></div>
                    <div className="mobile-card-info">
                      <div className="mobile-card-name">{displayName(item.PlayerName)}</div>
                      <div className={`mobile-card-sub${cluster === 'ALL' ? ' cluster-tag' : ''}`}>{tribeOrCluster(item)}</div>
                    </div>
                  </div>
                  <div className="mobile-stats">
                    <div className={`mobile-stat${sortedCls('Kills')}`}>
                      <div className="mobile-stat-label">Kills</div>
                      <div className="mobile-stat-value">{fmtInt(item.PlayerKills)}</div>
                    </div>
                    <div className={`mobile-stat${sortedCls('Deaths')}`}>
                      <div className="mobile-stat-label">Deaths</div>
                      <div className="mobile-stat-value">{fmtInt(item.DeathByPlayer)}</div>
                    </div>
                    <div className="mobile-stat">
                      <div className="mobile-stat-label">K/D</div>
                      <div className="mobile-stat-value">{formatKD(item.PlayerKills, item.DeathByPlayer)}</div>
                    </div>
                    <div className={`mobile-stat${sortedCls('Tamed Dino Kills')}`}>
                      <div className="mobile-stat-label">Tamed Dino Kills</div>
                      <div className="mobile-stat-value">{fmtInt(item.DinoKills)}</div>
                    </div>
                    <div className={`mobile-stat${sortedCls('Time Played')}`}>
                      <div className="mobile-stat-label">Time Played</div>
                      <div className="mobile-stat-value">{fmtHours(item.PlayTime)}<span className="unit"> hrs</span></div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={pagination.total_pages}
        onPageChange={setCurrentPage}
      />
      {selectedPlayer && (
        <PlayerDetailModal
          playerName={selectedPlayer}
          cluster={cluster}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </div>
  );
};

export default PlayerLeaderboard;
